import { useEffect, useState } from 'react'
import { CheckCircle2, Clock, Download, FileText, Loader2, RefreshCw, Search, XCircle } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToExcel } from '../../utils/exportExcel'
import ConfirmModal from '../ui/ConfirmModal'

const STATUS_OPTIONS = [
  { label: '全部状态', value: '' },
  { label: '待审核', value: 'pending_review' },
  { label: '已拒绝', value: 'rejected' },
  { label: '开票中', value: 'issuing' },
  { label: '已开票', value: 'issued' }
]

function normalizeInvoiceStatus(status) {
  return status === 'approved' ? 'issuing' : status
}

function getInvoiceStatusText(status) {
  switch (normalizeInvoiceStatus(status)) {
    case 'pending_review':
      return '待审核'
    case 'rejected':
      return '已拒绝'
    case 'issuing':
      return '开票中'
    case 'issued':
      return '已开票'
    default:
      return '未知'
  }
}

function getStatusBadge(status) {
  switch (normalizeInvoiceStatus(status)) {
    case 'pending_review':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />待审核</span>
    case 'rejected':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />已拒绝</span>
    case 'issuing':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"><Clock className="w-3 h-3 mr-1" />开票中</span>
    case 'issued':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" />已开票</span>
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">未知</span>
  }
}

function formatMoney(value) {
  const amount = Number(value || 0)
  return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatInvoiceType(titleType, invoiceType) {
  const title = titleType === 'company' ? '企业' : '个人'
  const type = invoiceType === 'special' ? '专票' : '普票'
  return `${title} ${type}`
}

export default function InvoiceApplicationsTab() {
  const [loading, setLoading] = useState(true)
  const [list, setList] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [status, setStatus] = useState('')
  const [phone, setPhone] = useState('')
  const [orderNo, setOrderNo] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [selectedDetail, setSelectedDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [issueModalOpen, setIssueModalOpen] = useState(false)
  const [issueSubmitting, setIssueSubmitting] = useState(false)
  const [issueForm, setIssueForm] = useState({
    id: null,
    invoiceNo: '',
    invoiceCode: '',
    adminRemark: '',
    file: null
  })

  const closeIssueModal = () => {
    setIssueModalOpen(false)
    setIssueForm({
      id: null,
      invoiceNo: '',
      invoiceCode: '',
      adminRemark: '',
      file: null
    })
  }

  const fetchList = async (page = 1) => {
    setLoading(true)
    try {
      const response = await api.get('/admin/invoices', {
        params: {
          page,
          limit: pagination.limit,
          status,
          phone,
          orderNo
        }
      })
      if (response.data.success) {
        const data = response.data.data || response.data
        setList(data.list || [])
        setPagination({
          page: data.page || page,
          limit: data.limit || pagination.limit,
          total: data.total || 0,
          totalPages: data.totalPages || 1
        })
      }
    } catch (error) {
      console.error('获取发票申请列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDetail = async (id) => {
    setSelectedId(id)
    setDetailLoading(true)
    try {
      const response = await api.get(`/admin/invoices/${id}`)
      if (response.data.success) {
        const detail = response.data.data || response.data
        setSelectedDetail(detail || null)
      }
    } catch (error) {
      console.error('获取发票申请详情失败:', error)
      setSelectedDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    fetchList(1)
  }, [])

  const refreshCurrent = async () => {
    await fetchList(pagination.page)
    if (selectedId) {
      await fetchDetail(selectedId)
    }
  }

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/invoices/export', {
        params: {
          status,
          phone,
          orderNo
        }
      })
      if (!response.data.success) return
      const rows = response.data.list || response.data.data?.list || []
      if (rows.length === 0) return

      exportRowsToExcel({
        fileName: `发票申请管理_${new Date().toISOString().slice(0, 10)}`,
        sheetName: '发票申请管理',
        columns: [
          { header: '申请单号', key: 'application_no', width: 24 },
          { header: '用户ID', key: 'user_id', width: 10 },
          { header: '用户昵称', key: 'nickname', width: 16 },
          { header: '手机号', key: 'phone', width: 14 },
          { header: '抬头类型', key: 'title_type', width: 10 },
          { header: '发票类型', key: 'invoice_type', width: 10 },
          { header: '发票抬头', key: 'invoice_title', width: 28 },
          { header: '接收邮箱', key: 'receiver_email', width: 24 },
          { header: '开票金额', key: 'amount', width: 12, format: '0.00' },
          { header: '状态', key: 'status', width: 10 },
          { header: '驳回原因', key: 'reject_reason', width: 24 },
          { header: '关联订单号', key: 'order_nos', width: 30 },
          { header: '申请时间', key: 'created_at', width: 20 }
        ],
        rows: rows.map((row) => ({
          application_no: row.application_no || '',
          user_id: row.user_id ?? '',
          nickname: row.nickname || row.username || '',
          phone: row.phone || '',
          title_type: row.title_type === 'company' ? '企业' : '个人',
          invoice_type: row.invoice_type === 'special' ? '专票' : '普票',
          invoice_title: row.invoice_title || '',
          receiver_email: row.receiver_email || '',
          amount: Number(row.amount || 0),
          status: getInvoiceStatusText(row.status),
          reject_reason: row.reject_reason || '',
          order_nos: Array.isArray(row.orders) ? row.orders.map((item) => item.order_no).join(', ') : '',
          created_at: row.created_at ? new Date(row.created_at).toLocaleString('zh-CN') : ''
        }))
      })
    } catch (error) {
      console.error('导出发票申请失败:', error)
    }
  }

  const handleApprove = async (id) => {
    if (!window.confirm('确认审核通过该开票申请，并直接进入开票中吗？')) return
    await api.post(`/admin/invoices/${id}/review`, { action: 'approve' })
    await refreshCurrent()
  }

  const handleReject = async (id) => {
    const reason = window.prompt('请输入驳回原因')
    if (!reason) return
    await api.post(`/admin/invoices/${id}/review`, { action: 'reject', reason })
    await refreshCurrent()
  }

  const openIssueModal = (id) => {
    setIssueForm({
      id,
      invoiceNo: '',
      invoiceCode: '',
      adminRemark: '',
      file: null
    })
    setIssueModalOpen(true)
  }

  const handleIssued = async () => {
    if (!issueForm.id) return
    if (!issueForm.file) {
      window.alert('请先上传发票文件')
      return
    }

    setIssueSubmitting(true)
    try {
      const submitData = new FormData()
      submitData.append('status', 'issued')
      submitData.append('invoice_file', issueForm.file)
      if (issueForm.invoiceNo.trim()) submitData.append('invoiceNo', issueForm.invoiceNo.trim())
      if (issueForm.invoiceCode.trim()) submitData.append('invoiceCode', issueForm.invoiceCode.trim())
      if (issueForm.adminRemark.trim()) submitData.append('adminRemark', issueForm.adminRemark.trim())

      await api.post(`/admin/invoices/${issueForm.id}/issue-status`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      closeIssueModal()
      await refreshCurrent()
    } catch (error) {
      window.alert(error.response?.data?.error || '提交已开票失败')
    } finally {
      setIssueSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">发票申请管理</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
          >
            {STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>{item.label}</option>
            ))}
          </select>
          <div className="relative">
            <input
              type="text"
              placeholder="手机号/设备号"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchList(1)}
              className="pl-9 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none w-40"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="订单号"
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchList(1)}
              className="pl-9 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none w-52"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
          <button
            onClick={() => fetchList(1)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
          >
            <Search className="w-4 h-4" />
            查询
          </button>
          <button
            onClick={() => refreshCurrent()}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            刷新
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="w-4 h-4" />
            导出 Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)] gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申请单/用户</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金额</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申请时间</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">暂无发票申请</td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr
                      key={row.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${selectedId === row.id ? 'bg-blue-50/70 dark:bg-blue-900/20' : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => fetchDetail(row.id)}
                          className="text-left"
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{row.application_no}</div>
                          <div className="text-xs text-gray-500">{row.nickname || row.username || '-'} / {row.phone || `ID:${row.user_id}`}</div>
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <div>{formatInvoiceType(row.title_type, row.invoice_type)}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[180px]" title={row.invoice_title}>{row.invoice_title}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                        {formatMoney(row.amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(row.status)}
                        {row.reject_reason ? (
                          <div className="text-xs text-gray-500 mt-1 max-w-[180px] truncate" title={row.reject_reason}>{row.reject_reason}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.created_at ? new Date(row.created_at).toLocaleString('zh-CN') : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => fetchDetail(row.id)}
                            className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            详情
                          </button>
                          {row.status === 'pending_review' ? (
                            <>
                              <button onClick={() => handleApprove(row.id)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs">通过</button>
                              <button onClick={() => handleReject(row.id)} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs">驳回</button>
                            </>
                          ) : null}
                          {normalizeInvoiceStatus(row.status) === 'issuing' ? (
                            <button onClick={() => openIssueModal(row.id)} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs">已开票</button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500">共 {pagination.total} 条</div>
            <div className="flex items-center gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchList(pagination.page - 1)}
                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-50"
              >
                上一页
              </button>
              <span className="text-sm text-gray-500">{pagination.page} / {pagination.totalPages}</span>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchList(pagination.page + 1)}
                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">申请详情</h3>
          </div>

          {!selectedId ? (
            <div className="text-sm text-gray-500">请选择左侧任意一条发票申请查看详情</div>
          ) : detailLoading ? (
            <div className="py-10 text-center text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : !selectedDetail ? (
            <div className="text-sm text-gray-500">暂无详情数据</div>
          ) : (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-gray-500">申请单号</div>
                <div className="text-gray-900 dark:text-white break-all">{selectedDetail.application_no}</div>
              </div>
              <div>
                <div className="text-gray-500">用户</div>
                <div className="text-gray-900 dark:text-white">{selectedDetail.nickname || selectedDetail.username || '-'} / {selectedDetail.phone || `ID:${selectedDetail.user_id}`}</div>
              </div>
              <div>
                <div className="text-gray-500">订单号</div>
                <div className="text-gray-900 dark:text-white break-all">
                  {(selectedDetail.orders || []).map((item) => item.order_no).join(', ') || '-'}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-500">开票金额</div>
                  <div className="text-gray-900 dark:text-white">{formatMoney(selectedDetail.amount)}</div>
                </div>
                <div>
                  <div className="text-gray-500">当前状态</div>
                  <div>{getStatusBadge(selectedDetail.status)}</div>
                </div>
              </div>
              <div>
                <div className="text-gray-500">抬头类型 / 发票类型</div>
                <div className="text-gray-900 dark:text-white">{formatInvoiceType(selectedDetail.title_type, selectedDetail.invoice_type)}</div>
              </div>
              <div>
                <div className="text-gray-500">发票抬头</div>
                <div className="text-gray-900 dark:text-white break-all">{selectedDetail.invoice_title}</div>
              </div>
              <div>
                <div className="text-gray-500">接收邮箱</div>
                <div className="text-gray-900 dark:text-white break-all">{selectedDetail.receiver_email}</div>
              </div>
              {selectedDetail.taxpayer_id ? (
                <div>
                  <div className="text-gray-500">纳税人识别号</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.taxpayer_id}</div>
                </div>
              ) : null}
              {selectedDetail.bank_name ? (
                <div>
                  <div className="text-gray-500">基本开户银行</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.bank_name}</div>
                </div>
              ) : null}
              {selectedDetail.bank_account ? (
                <div>
                  <div className="text-gray-500">基本开户账号</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.bank_account}</div>
                </div>
              ) : null}
              {selectedDetail.register_address ? (
                <div>
                  <div className="text-gray-500">企业注册地址</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.register_address}</div>
                </div>
              ) : null}
              {selectedDetail.register_phone ? (
                <div>
                  <div className="text-gray-500">企业注册电话</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.register_phone}</div>
                </div>
              ) : null}
              {selectedDetail.reject_reason ? (
                <div>
                  <div className="text-gray-500">驳回原因</div>
                  <div className="text-red-600 break-all">{selectedDetail.reject_reason}</div>
                </div>
              ) : null}
              {selectedDetail.invoice_no ? (
                <div>
                  <div className="text-gray-500">发票号码</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.invoice_no}</div>
                </div>
              ) : null}
              {selectedDetail.invoice_code ? (
                <div>
                  <div className="text-gray-500">发票代码</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.invoice_code}</div>
                </div>
              ) : null}
              {selectedDetail.invoice_file_url ? (
                <div>
                  <div className="text-gray-500">发票文件</div>
                  <a className="text-blue-600 break-all" href={selectedDetail.invoice_file_url} target="_blank" rel="noreferrer">{selectedDetail.invoice_file_url}</a>
                </div>
              ) : null}
              {selectedDetail.admin_remark ? (
                <div>
                  <div className="text-gray-500">后台备注</div>
                  <div className="text-gray-900 dark:text-white break-all">{selectedDetail.admin_remark}</div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={issueModalOpen}
        onClose={closeIssueModal}
        onConfirm={handleIssued}
        title="上传发票并提交"
        message="上传完成后提交，该申请会直接进入已开票状态。支持 PDF、OFD、JPG、PNG、WEBP、BMP、TIF、TIFF。"
        confirmText="提交已开票"
        cancelText="取消"
        confirmStyle="success"
        loading={issueSubmitting}
      >
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">发票文件</label>
            <input
              type="file"
              accept=".pdf,.ofd,.png,.jpg,.jpeg,.webp,.bmp,.tif,.tiff"
              onChange={(e) => setIssueForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
            />
            <div className="mt-1 text-xs text-gray-500">
              {issueForm.file ? `已选择：${issueForm.file.name}` : '必传，最大 20MB'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">发票号码</label>
            <input
              type="text"
              value={issueForm.invoiceNo}
              onChange={(e) => setIssueForm((prev) => ({ ...prev, invoiceNo: e.target.value }))}
              placeholder="可选"
              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">发票代码</label>
            <input
              type="text"
              value={issueForm.invoiceCode}
              onChange={(e) => setIssueForm((prev) => ({ ...prev, invoiceCode: e.target.value }))}
              placeholder="可选"
              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">后台备注</label>
            <textarea
              rows={3}
              value={issueForm.adminRemark}
              onChange={(e) => setIssueForm((prev) => ({ ...prev, adminRemark: e.target.value }))}
              placeholder="可选"
              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white resize-y"
            />
          </div>
        </div>
      </ConfirmModal>
    </div>
  )
}
